import React, { useRef, useState } from 'react';
import { Callout } from 'components/layout/Callout';
import { Button, IconButton } from 'components/buttons';
import { HelpCenterHeader } from './HelpCenterHeader';
import {
  getActions,
  getCanReRunValidations,
  getErrorsOpen,
  getLocation,
  getShowIgnored,
  getWarningsOpen,
  useHelpCenterStore,
  useHelpCenterStore as useStore,
} from './HelpCenterStore';
import { ActionsCallout, IActionsCalloutProps } from 'components/actions/ActionsCallout';
import { ValidationEntry } from './ValidationEntry';
import { BreakLevel } from '@decisions/server/DecisionsFramework/BreakLevel';
import { HelpCenterLocation } from './HelpCenterLocation';
import classNames from 'classnames';

interface IHelpCenterValidationLayerProps {
  entries?: ValidationEntry[];
  sourceRef: React.MutableRefObject<HTMLElement | null>;
  sourceClone?: React.FunctionComponentElement<any>;
}

/**
 * Validation Overlay layer.
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */

const getClassName = (isOpen: boolean) =>
  classNames('dp-help-center-validation-layer__entry-actions', {
    'dp-help-center-validation-layer__entry-actions--active': isOpen,
  });

const getEntryClassName = (active: boolean) =>
  classNames('dp-help-center-validation-layer__entry', {
    'dp-help-center-validation-layer__entry--active': active,
  });

export function HelpCenterValidationLayer({ entries, ...calloutProps }: IHelpCenterValidationLayerProps) {
  const { toggleWarnings, toggleErrors, ignoreAction, goToAction } = useStore(getActions);

  const actionButtonRef: React.MutableRefObject<HTMLElement | null> = useRef(null);
  const [actionsProps, setActionsProps] = useState<IActionsCalloutProps>();
  const showIgnored = useStore(getShowIgnored);
  const [activeIssue, setActiveIssue] = useState<ValidationEntry | undefined>(undefined);
  const warningsOpen = useStore(getWarningsOpen);
  const errorsOpen = useStore(getErrorsOpen);
  const helpCenterLocation = useStore(getLocation);
  const canReRunValidations = useHelpCenterStore(getCanReRunValidations);

  const onClose = warningsOpen ? toggleWarnings : toggleErrors;
  const fromPortal = helpCenterLocation === HelpCenterLocation.Portal;
  let title = '';
  let ignoredCount = 0;
  let count = 0;
  let hasIgnoreAction = false;
  let onValidationActions: (issue: ValidationEntry) => void;

  if (fromPortal) {
    title = warningsOpen ? 'System Warnings' : 'System Validations';
    onValidationActions = (issue: ValidationEntry) => {
      // BaseActionType
      setActionsProps({
        entityId: issue.id,
        entityType: issue.entityTypeName ?? '',
        sourceRef: actionButtonRef,
        onAction: () => {
          setActiveIssue(undefined);
          setActionsProps(undefined);
          onClose();
        },
        onClose: () => {
          setActiveIssue(undefined);
          setActionsProps(undefined);
        },
      });
    };
  } else {
    // TODO should use useMemo for filtering if we have performance issues
    entries = entries?.filter(e => e.level === (warningsOpen ? BreakLevel.Warning : BreakLevel.Fatal));
    hasIgnoreAction = entries?.some(entry => entry.ignoreAction) ?? false;
    count = entries?.length ?? 0;
    ignoredCount = entries?.filter(e => e.isIgnored).length ?? 0;

    if (!showIgnored) {
      entries = entries?.filter(e => !e.isIgnored);
    } else {
      entries = entries?.filter(e => e.isIgnored);
    }
  }

  return (
    <Callout
      className="dp-help-center-validation-layer"
      showArrow={true}
      isOpen={warningsOpen || errorsOpen}
      {...calloutProps}
      placement="right-end"
      onClose={onClose}
      backdropIsVisible={true}
      scrollContents={false}
    >
      {(hasIgnoreAction || canReRunValidations || fromPortal) && (
        <HelpCenterHeader count={count} ignoredCount={ignoredCount} title={title} hasIgnoreAction={hasIgnoreAction} />
      )}
      <ul className="dp-help-center-validation-layer__entries">
        {entries &&
          entries.map(entry => (
            <li key={entry.id} className={getEntryClassName(entry === activeIssue)}>
              {fromPortal && (
                <img
                  className="dp-help-center-validation-layer__entry-icon"
                  src={`${virtualPath}${entry.imageUrl}`}
                  alt={''}
                />
              )}
              <div className="dp-help-center-validation-layer__entry-contents">
                <h5 className="dp-help-center-validation-layer__entry-title">
                  <span title={entry.title} className="dp-help-center-validation-layer__entry-title-text">
                    {entry.title}
                  </span>
                </h5>
                {fromPortal && (
                  <div className="dp-help-center-validation-layer__entry-type">[{entry.validationItemType}]</div>
                )}
                <div title={entry.description} className="dp-help-center-validation-layer__entry-details">
                  {entry.description}
                </div>
              </div>
              {fromPortal && (
                <IconButton
                  className={getClassName(activeIssue === entry)}
                  iconClassName="dp-icon--vertical-dots"
                  iconSize="native"
                  onClick={e => {
                    setActiveIssue(entry);
                    actionButtonRef.current = e.currentTarget;
                    onValidationActions(entry);
                  }}
                />
              )}
              {(entry.ignoreAction || entry.gotoAction) && (
                <div className="dp-help-center-validation-layer__designer-entry-actions">
                  {entry.gotoAction && (
                    <Button textTransform={'capitalize'} variant="link" onClick={() => goToAction(entry)}>
                      Edit
                    </Button>
                  )}
                  {entry.ignoreAction && (
                    <Button textTransform={'capitalize'} variant="link" onClick={() => ignoreAction(entry)}>
                      {entry.isIgnored ? "Don't Ignore" : 'Ignore'}
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
      </ul>
      {actionsProps && <ActionsCallout {...actionsProps} placement="right" />}
    </Callout>
  );
}
